(() => {
  const config = JSON.parse(document.querySelector('[data-commerce-config]')?.textContent || '{}');
  const root = (config.root || '/').replace(/\/?$/, '/');
  const moneyFormat = document.querySelector('[data-money-format]')?.dataset.moneyFormat;
  const money = cents => (window.RosviaMoney ? window.RosviaMoney(cents, moneyFormat) : `€${(cents / 100).toFixed(2)}`);

  // Re-render the drawer and the cart page from Shopify so totals and discounts are always the real ones.
  let refreshing = null;
  const refreshSections = async () => {
    const drawer = document.querySelector('cart-drawer');
    const page = document.querySelector('[data-main-cart]');
    const ids = [drawer?.dataset.sectionId, page?.dataset.sectionId].filter(Boolean);
    if (!ids.length) return;
    // Section Rendering API: request from the root URL without a JSON Accept header, otherwise /cart answers with cart.js.
    const response = await fetch(`${root}?sections=${ids.join(',')}`);
    if (!response.ok) throw new Error(config.error);
    const sections = await response.json();
    const parse = html => new DOMParser().parseFromString(html, 'text/html');
    if (drawer && sections[drawer.dataset.sectionId]) {
      const fresh = parse(sections[drawer.dataset.sectionId]).querySelector('[data-cart-drawer-content]');
      const current = drawer.querySelector('[data-cart-drawer-content]');
      if (fresh && current) {
        const focusKey = document.activeElement?.closest('[data-line-key]')?.dataset.lineKey;
        const focusAttr = document.activeElement?.hasAttribute('data-quantity-plus') ? '[data-quantity-plus]' : document.activeElement?.hasAttribute('data-quantity-minus') ? '[data-quantity-minus]' : null;
        // The body scrolls on larger screens, the whole shell on phones (cart-drawer.css); keep both positions.
        const scroll = current.querySelector('.cart-drawer__body')?.scrollTop || 0;
        const shellScroll = current.scrollTop;
        current.replaceWith(fresh);
        const body = fresh.querySelector('.cart-drawer__body'); if (body) body.scrollTop = scroll;
        fresh.scrollTop = shellScroll;
        const target = focusKey && focusAttr ? fresh.querySelector(`[data-line-key="${CSS.escape(focusKey)}"] ${focusAttr}`) : null;
        (target || (drawer.dialog?.open ? fresh.querySelector('[data-drawer-close]') : null))?.focus({ preventScroll: true });
        loadRecommendations(fresh.querySelector('[data-cart-recommendations]'));
      }
    }
    if (page && sections[page.dataset.sectionId]) {
      const fresh = parse(sections[page.dataset.sectionId]);
      const hasLines = fresh.querySelector('[data-cart-region="lines"]');
      if (!hasLines || !page.querySelector('[data-cart-region="lines"]')) { page.replaceWith(fresh.querySelector('[data-main-cart]')); document.dispatchEvent(new CustomEvent('cart:page-rendered')); return; }
      page.querySelectorAll('[data-cart-region]').forEach(region => {
        const replacement = fresh.querySelector(`[data-cart-region="${region.dataset.cartRegion}"]`);
        if (replacement) region.innerHTML = replacement.innerHTML;
      });
    }
  };
  const scheduleRefresh = () => {
    refreshing = (refreshing || Promise.resolve()).then(refreshSections).catch(() => {});
    return refreshing;
  };
  document.addEventListener('cart:updated', scheduleRefresh);

  // Line quantity and removal — shared by the drawer and the cart page.
  const changeLine = (key, quantity, element) => {
    if (!window.RosviaCart || !key) return;
    const line = element.closest('[data-line-key]');
    line?.setAttribute('aria-busy', 'true');
    window.RosviaCart.change(key, quantity).catch(error => {
      const status = document.querySelector('[data-cart-status]');
      if (status) status.textContent = error.message || config.error;
      scheduleRefresh();
    }).finally(() => line?.removeAttribute('aria-busy'));
  };
  document.addEventListener('change', event => {
    const input = event.target.closest('[data-cart-line-quantity]');
    if (input) changeLine(input.dataset.key, Math.max(0, Number(input.value) || 0), input);
  });
  document.addEventListener('click', event => {
    const remove = event.target.closest('[data-cart-line-remove]');
    if (!remove) return;
    event.preventDefault();
    changeLine(remove.dataset.key, 0, remove);
  });

  // Complementary products from Shopify's recommendation engine; hidden when there are none.
  // Cached per URL so a drawer re-render after every cart change repopulates instantly instead of flickering empty.
  const recommendationCache = new Map();
  const loadRecommendations = async container => {
    if (!container || container.dataset.loaded) return;
    container.dataset.loaded = 'true';
    const base = container.dataset.recommendationsUrl || document.querySelector('cart-drawer')?.dataset.recommendationsUrl;
    const exclude = new Set((container.dataset.exclude || '').split(',').filter(Boolean));
    const limit = Number(container.dataset.limit) || 3;
    const fetchIntent = intent => {
      const url = `${base}?product_id=${container.dataset.productId}&limit=${limit + exclude.size}&intent=${intent}`;
      if (!recommendationCache.has(url)) recommendationCache.set(url, fetch(url).then(response => (response.ok ? response.json() : {})).then(data => data.products || []).catch(() => []));
      return recommendationCache.get(url);
    };
    try {
      let products = (await fetchIntent('complementary')).filter(product => !exclude.has(String(product.id)) && product.available);
      if (products.length < limit) products = products.concat((await fetchIntent('related')).filter(product => !exclude.has(String(product.id)) && product.available && !products.some(item => item.id === product.id)));
      products = products.slice(0, limit);
      if (!products.length) return;
      const list = container.querySelector('[data-recommendation-list]');
      const template = container.querySelector('[data-recommendation-template]');
      products.forEach(product => {
        const item = template.content.firstElementChild.cloneNode(true);
        const variant = product.variants.find(entry => entry.available) || product.variants[0];
        item.querySelectorAll('[data-rec-url]').forEach(link => { link.href = product.url; });
        const image = item.querySelector('[data-rec-image]');
        const src = product.featured_image || product.images?.[0];
        if (src) image.src = `${src}${src.includes('?') ? '&' : '?'}width=240`; else image.remove();
        item.querySelector('[data-rec-title]').textContent = product.title;
        item.querySelector('[data-rec-price]').textContent = money(variant.price);
        item.querySelector('[data-rec-variant]').value = variant.id;
        list.append(item);
      });
      container.hidden = false;
    } catch {}
  };

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.addEventListener('click', event => {
        if (event.target === this.dialog || event.target.closest('[data-drawer-close]')) this.close();
      });
      this.dialog.addEventListener('close', () => { document.body.classList.remove('cart-drawer-open'); this.opener?.focus?.({ preventScroll: true }); });
      document.addEventListener('click', event => {
        const opener = event.target.closest('[data-cart-drawer-open], .site-header__cart');
        if (!opener || event.metaKey || event.ctrlKey || event.shiftKey) return;
        if (opener.matches('.site-header__cart') && (this.dataset.interceptHeader !== 'true' || this.dataset.pageType === 'cart')) return;
        event.preventDefault();
        this.open(opener);
      });
      document.addEventListener('cart-drawer:open', () => this.open());
      document.addEventListener('cart:added', () => {
        if (this.dataset.openOnAdd !== 'true' || this.dataset.pageType === 'cart') return;
        const toast = document.querySelector('[data-cart-toast]');
        if (toast) toast.hidden = true;
        this.open(document.activeElement);
      });
    }
    open(opener) {
      if (this.dialog.open) return;
      this.opener = opener;
      this.dialog.showModal();
      document.body.classList.add('cart-drawer-open');
      loadRecommendations(this.querySelector('[data-cart-recommendations]'));
    }
    close() { this.dialog.close(); }
  }
  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

  const initPage = () => loadRecommendations(document.querySelector('[data-main-cart] [data-cart-recommendations]'));
  document.addEventListener('cart:page-rendered', initPage);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initPage); else initPage();
})();
